import React from 'react'
import ReactMarkdown from 'react-markdown'

import {
  BANNER_MODE_ICONS,
  UI2BannerConfigProps,
  UI2_BANNER_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/display/ui2-banner/ui2-banner.config'
import { UI2BannerInternalProps } from 'shared-libs/components/ui2/display/ui2-banner/ui2-banner.internal'
import { getEffectiveTranslationTable } from 'shared-libs/components/ui2/fields/modules/ui2-base-field/ui2-base-field.internal'
import { UI2_BANNER_THEME } from 'shared-libs/components/ui2/theme/theme-registry'
import { resolveUI2Color } from 'browser/components/ui2/ui2-colors'
import { translateString } from 'shared-libs/helpers/utils'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import './_ui2-banner.scss'

type IUI2BannerProps = UI2BannerConfigProps & UI2BannerInternalProps

/**
 * Inline message banner for UI2 forms and fields. Renders a mode-driven icon,
 * optional title and markdown/plain body, drawing its tone treatment from the
 * shared theme registry; used for info/success/warning/error callouts.
 */
export const UI2Banner: React.FC<IUI2BannerProps> = (props) => {
  const { text, markdownText, mode, title, icon, children } = props
  const resolvedMode = mode ?? UI2_BANNER_DEFAULT_PROPS.mode
  const iconName = icon ?? BANNER_MODE_ICONS[resolvedMode]
  const treatment = UI2_BANNER_THEME[resolvedMode]
  const treatmentStyle = {
    backgroundColor: resolveUI2Color(treatment.background),
    color: resolveUI2Color(treatment.foreground),
  }
  const resolvedTable = getEffectiveTranslationTable(props)
  const translatedTitle = title ? translateString(title, resolvedTable) ?? title : undefined
  const translatedMarkdown = markdownText
    ? translateString(markdownText, resolvedTable) ?? markdownText
    : undefined
  const translatedText = text ? translateString(text, resolvedTable) ?? text : undefined
  const body = translatedMarkdown ?? translatedText

  return (
    <div className='ui2-banner' style={treatmentStyle}>
      <div className="ui2-banner__header">
        <UI2Icon name={iconName} size={16} />
        {translatedTitle && <span className="ui2-banner__title">{translatedTitle}</span>}
      </div>
      {body && (
        <div className="ui2-banner__body">
          {translatedMarkdown ? <ReactMarkdown>{body}</ReactMarkdown> : body}
        </div>
      )}
      {children && <div className="ui2-banner__actions">{children}</div>}
    </div>
  )
}
